import { ActionTypeEnum, EStatus, IBody, IJson, Routes, TActionData, TWAction } from '@libs/types';
import { AccessDeniedError } from '../errors/AccessDeniedError';
import { CFAccessDeniedError } from '../errors/CFAccessDeniedError';
import { sharedAbortController } from '../SharedAbortController';
import { sharedTimeoutId } from '../SharedTimeoutId';
import { getHost, isCFForbidden, isLoadingAction } from '../utils';

// Type definitions for User-Agent Client Hints API
// https://wicg.github.io/ua-client-hints/
declare global {
    interface NavigatorUAData {
        readonly brands: readonly NavigatorUABrandVersion[];
        readonly mobile: boolean;
        readonly platform: string;
        getHighEntropyValues(hints: string[]): Promise<UADataValues>;
    }

    interface UADataValues {
        readonly brands?: readonly NavigatorUABrandVersion[];
        readonly mobile?: boolean;
        readonly platform?: string;
        readonly architecture?: string;
        readonly model?: string;
        readonly platformVersion?: string;
        readonly uaFullVersion?: string;
        readonly bitness?: string;
        readonly fullVersionList?: NavigatorUABrandVersion[];
        readonly wow64?: boolean;
        /** 'Desktop' | 'Mobile' | 'Tablet' | 'TV' | 'Watch' | 'XR' | 'EInk' | 'Automotive' — Chromium 132+ */
        readonly formFactors?: readonly string[];
    }

    interface NavigatorUABrandVersion {
        readonly brand: string;
        readonly version: string;
    }

    interface Navigator {
        readonly userAgentData?: NavigatorUAData;
    }
}
type TOperatingSystem = {
    name?: string;
    version?: string;
    architecture?: string;
    model?: string;
    /** UA Client Hints form factors, when the browser exposes them. Used to derive `IEnvironmentData.deviceType`. */
    formFactors?: readonly string[];
};

const MAX_ATTEMPTS = Number(process.env.NX_PUBLIC_FETCH_SCENARIO_MAX_ATTEMPTS ?? 3);
export const IS_SUPPORT_MULTI_TABS = Boolean(process.env.NX_PUBLIC_SUPPORT_MULTI_TABS ?? false);
export const AUTH = 'auth';
const KEEPALIVE_BODY_LIMIT = 64 * 1024; // 64kb

export const isValueObject = (value: unknown) => {
    return value && typeof value === 'object' && !Array.isArray(value);
};
const isValueNumber = (value: unknown) => {
    return typeof value === 'number' && !Number.isNaN(value);
};
const isValueBoolean = (value: unknown) => {
    return typeof value === 'boolean';
};

export const getActionData = <T>(widgets?: T): TActionData => {
    const actionData: TActionData = {};
    if (Array.isArray(widgets)) {
        widgets.forEach((w) => {
            const value = w?.data?.value;
            if (w?.uid && (value || isValueNumber(value) || isValueBoolean(value))) {
                actionData[w.uid] = value;
            }
        });
    }
    return actionData;
};

const isWidgetAction = (action: TWAction | undefined) => action?.type === ActionTypeEnum.Widget;
const getKey = (action: TWAction | undefined, widgetUid: string | undefined, playerId?: string) =>
    `${playerId ?? 'undefined'}_${widgetUid ?? 'undefined'}_${action?.name}`;
const checkAndAbortPreviousRequests = (action: TWAction | undefined, widgetUid: string | undefined, playerId?: string): AbortController | undefined => {
    if (isWidgetAction(action)) {
        const abortKey = getKey(action, widgetUid, playerId);
        return sharedAbortController.setNewWidgetAbortController(abortKey, playerId);
    } else {
        const controller = sharedAbortController.getScenarioAbortController(playerId);
        if (controller) {
            try {
                controller.abort();
            } catch (e) {
                console.warn('Cannot abort previous request: ', e);
            }
        }
        sharedAbortController.setNewScenarioAbortController(playerId);
        return sharedAbortController.getScenarioAbortController(playerId);
    }
};
const checkAndClearPreviousTimeout = (action: TWAction | undefined, widgetUid: string | undefined, playerId?: string) => {
    const timeoutIdKey = getKey(action, widgetUid, playerId);
    sharedTimeoutId.clearWidgetTimeoutId(timeoutIdKey);
};

export const getOSName = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Win')) return 'Windows';
    if (userAgent.includes('Mac')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    // iOS devices (iPhone, iPad) often have "like Mac" in their user agent
    if (/iPad|iPhone|iPod/.test(userAgent)) return 'iOS';
    return 'Unknown';
};

export const getOsDetails = async (): Promise<TOperatingSystem> => {
    if (navigator.userAgentData) {
        const uaData = await navigator.userAgentData.getHighEntropyValues(['platform', 'platformVersion', 'architecture', 'model', 'formFactors']);
        return {
            name: uaData.platform,
            version: uaData.platformVersion,
            architecture: uaData.architecture,
            model: uaData.model,
            formFactors: uaData.formFactors,
        };
    }
    return {
        name: getOSName(),
    };
};

export const getScreenPrint = (): string => {
    return (
        `Current Resolution: ${window?.screen?.width}x${window?.screen?.height}, ` +
        `Available Resolution: ${window?.screen?.availWidth}x${window?.screen?.availHeight}, ` +
        `Color Depth: ${window?.screen?.colorDepth}, ` +
        `Device XDPI: ${window?.devicePixelRatio}, ` +
        `Device YDPI: ${window?.devicePixelRatio}`
    );
};

const delay = (
    action: TWAction | undefined,
    widgetUid: string | undefined,
    isShowLoader: boolean,
    setPlayerState: (status: EStatus) => void,
    playerId?: string,
): Promise<void> => {
    return new Promise((resolve) => {
        if (action?.delay && action.delay > 0) {
            if (!isLoadingAction(action)) {
                setPlayerState(EStatus.Delay);
            }
            checkAndClearPreviousTimeout(action, widgetUid, playerId);
            const timeoutId = setTimeout(() => {
                if (isShowLoader) {
                    setPlayerState(EStatus.Loading);
                }
                resolve();
            }, action.delay);
            sharedTimeoutId.setNewWidgetTimeoutId(getKey(action, widgetUid, playerId), timeoutId);
        } else {
            resolve();
        }
    });
};

// const getAction = (action: TWAction | undefined): TWAction | undefined => {
//     const doNotHaveAuth = !sessionStorage.getItem(AUTH);
//     if (!action && doNotHaveAuth) {
//         const lastAction = localStorage.getItem(LAST_ACTION);
//         if (lastAction) {
//             return JSON.parse(lastAction);
//         }
//     }
//     return action;
// };

const getResponseBody = async (response: Response) => {
    const contentType = response.headers.get('Content-Type') || response.headers.get('content-type') || 'text/html';
    let body = 'Other';
    let isJson = false;
    if (contentType.includes('application/json')) {
        body = await response.json();
        isJson = true;
    } else if (contentType.includes('text/html')) {
        body = await response.text();
    }
    return { body, isJson };
};

const processCF = async (response: Response) => {
    const forbiddenBody = await getResponseBody(response);
    throw new CFAccessDeniedError('Need to start challenge!', forbiddenBody.body);
};

export const fetchData = async (url: string, body: IBody, keepalive?: boolean, abortController?: AbortController): Promise<IJson> | never => {
    const bodyAsString = JSON.stringify(body);
    let finalKeepalive = keepalive ?? false;
    if (finalKeepalive && bodyAsString.length > KEEPALIVE_BODY_LIMIT) {
        console.warn('fetchData: body size is too large for keepalive, switching to regular request.');
        finalKeepalive = false;
    }
    const options: RequestInit = {
        method: 'POST',
        body: bodyAsString,
        redirect: 'manual',
        keepalive: finalKeepalive,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    };
    if (abortController) {
        options.signal = abortController.signal;
    }
    const response = await fetch(url, options).catch((e) => {
        console.error('fetchData: cannot get response from orchestrator.', e);
        throw e;
    });
    if (isCFForbidden(response)) {
        await processCF(response);
    } else if (response.status === 403) {
        throw new AccessDeniedError(response.statusText);
    } else if (response.status !== 200) {
        console.error('fetchData: response status code invalid, code: ', response.status, response);
        throw Error(response.statusText);
    }
    return (await response.json()) as IJson;
};

export const fetchWithDelay = async (
    body: IBody,
    action: TWAction | undefined,
    isShowLoader: boolean,
    widgetUid: string | undefined,
    setPlayerState: (status: EStatus) => void,
    keepalive?: boolean,
    playerId?: string,
): Promise<IJson> | never => {
    const url = `${getHost()}${action ? Routes.POST_PROCESS_ACTION : Routes.POST_SCENARIO_PAGE}`;
    const abortController = checkAndAbortPreviousRequests(action, widgetUid, playerId);
    if (action) {
        await delay(action, widgetUid, isShowLoader, setPlayerState, playerId);
    }
    return await fetchData(url, body, keepalive, abortController);
};

/**
 * Whether the request may be sent again after a failure.
 *
 * Retrying is pointless without a connection: every attempt fails the same way, it only burns
 * `MAX_ATTEMPTS` and postpones the offline UI. So while the device is offline we never retry and
 * let the error surface immediately (the thunks mark it with `isOffline` and the player shows the
 * offline flow instead of the generic error screen).
 */
export const isCanDoRetry = (action: TWAction | undefined, attempt: number) => {
    if (!navigator.onLine) {
        return false;
    }
    return (!action || action.type === ActionTypeEnum.Scenario) && attempt < MAX_ATTEMPTS;
};
export const hasScenario = (json: IJson) => {
    return !!isValueObject(json?.scenario) && Object.keys(json?.scenario).length > 0;
};
export const hasScenarioWidgets = (json: IJson) => {
    return Array.isArray(json?.scenario?.pages?.[0]?.widgets) && json?.scenario?.pages[0].widgets.length > 0;
};
